import { apiFetch, isDemoMode } from "./api";

/**
 * Broadcasts: an approved newsletter sent to everyone who follows its
 * categories, by our own send engine (Worker + Amazon SES). See
 * apps/worker/src/broadcasts.ts for the routes.
 */

export type BroadcastStatus = "draft" | "scheduled" | "sending" | "sent" | "cancelled" | "failed";

export interface BroadcastTotals {
  recipients: number;
  queued: number;
  sending: number;
  sent: number;
  failed: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
}

export interface Broadcast {
  id: string;
  name: string;
  newsletter_id: string;
  status: BroadcastStatus;
  categories: string[];
  subject: string | null;
  preheader: string | null;
  scheduled_at: string | null;
  started_at: string | null;
  sent_at: string | null;
  cancelled_at: string | null;
  recipient_count: number;
  error: string | null;
  created_at: string;
  totals: BroadcastTotals | null;
}

export interface BroadcastDetail extends Broadcast {
  links: { idx: number; url: string; clicks: number; unique_clicks: number }[];
  variants: { category: string; subject: string; position: number }[];
}

export interface DeliveryStatus {
  ready: boolean;
  missing: string[];
  from: string;
}

async function call<T>(path: string, init?: RequestInit): Promise<{ data: T; meta: { delivery?: DeliveryStatus } }> {
  const r = await apiFetch(path, init);
  const body = (await r.json().catch(() => ({}))) as { data?: T; meta?: { delivery?: DeliveryStatus }; error?: string };
  if (!r.ok) throw new Error(body.error || `Request failed (${r.status})`);
  return { data: body.data as T, meta: body.meta ?? {} };
}

const post = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

/**
 * In demo mode there is no send engine and no invented sends to show: the
 * list is empty and says so, rather than failing.
 */
export async function fetchBroadcasts(): Promise<{ data: Broadcast[]; meta: { delivery?: DeliveryStatus; demo?: boolean } }> {
  if (await isDemoMode()) return { data: [], meta: { demo: true } };
  return call<Broadcast[]>("/api/v1/broadcasts");
}
export const fetchBroadcast = (id: string) => call<BroadcastDetail>(`/api/v1/broadcasts/${id}`);

export const fetchAudienceSize = (categories: string[]) =>
  call<{ total: number; by_category: { category: string; recipients: number }[] }>(
    "/api/v1/broadcasts/audience", post({ categories }));

export const createBroadcast = (newsletter_id: string, categories: string[]) =>
  call<Broadcast>("/api/v1/broadcasts", post({ newsletter_id, categories }));

export const sendBroadcastTest = (id: string, to: string[], category?: string) =>
  call<{ sent: string[]; via: string }>(`/api/v1/broadcasts/${id}/test`, post({ to, category }));

export const scheduleBroadcast = (id: string, sendAt: string | null, confirmRecipients: number) =>
  call<Broadcast>(`/api/v1/broadcasts/${id}/schedule`, post({ send_at: sendAt, confirm_recipients: confirmRecipients }));

export const cancelBroadcast = (id: string) => call<Broadcast>(`/api/v1/broadcasts/${id}/cancel`, post({}));

export async function deleteBroadcast(id: string): Promise<void> {
  await call<unknown>(`/api/v1/broadcasts/${id}`, { method: "DELETE" });
}

export const broadcastPreviewUrl = (id: string, category?: string) =>
  `/api/v1/broadcasts/${id}/preview${category ? `?category=${encodeURIComponent(category)}` : ""}`;

/** Share of recipients, as "24.2%"; a dash when nothing has gone out. */
export function rate(part: number | undefined, whole: number | undefined): string {
  if (!whole || part === undefined) return "—";
  return `${((part / whole) * 100).toFixed(1)}%`;
}

export interface AnalyticsRow {
  id: string; name: string; status: BroadcastStatus; categories: string[]; sent_at: string | null;
  recipients: number; sent: number; delivered: number; opened: number; clicked: number;
  bounced: number; complained: number; unsubscribed: number; failed: number;
}
export interface Analytics {
  days: number;
  totals: Omit<AnalyticsRow, "id" | "name" | "status" | "categories" | "sent_at"> & { broadcasts: number };
  broadcasts: AnalyticsRow[];
  links: { url: string; clicks: number; unique_clicks: number }[];
}

/** Real results of what we sent. Demo mode has no sends, so it is empty there too. */
export async function fetchAnalytics(days: number): Promise<Analytics> {
  if (await isDemoMode()) {
    const zero = { recipients: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, complained: 0, unsubscribed: 0, failed: 0 };
    return { days, totals: { broadcasts: 0, ...zero }, broadcasts: [], links: [] };
  }
  return (await call<Analytics>(`/api/v1/analytics?days=${days}`)).data;
}
